import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Globe, User, Lock, FolderOpen, Download, CheckCircle, AlertCircle, Loader2, Shield, Server, FileText } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const DeployPage = () => {
  const [protocol, setProtocol] = useState('ftp');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('21');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remoteDir, setRemoteDir] = useState('/public_html');
  const [deploying, setDeploying] = useState(false);
  const [result, setResult] = useState(null);
  const [packageInfo, setPackageInfo] = useState(null);
  const [envConfigured, setEnvConfigured] = useState(false);
  const [envPasswordSet, setEnvPasswordSet] = useState(false);

  const API = getApiBase();

  useEffect(() => {
    fetch(`${API}/api/deploy/package-info`)
      .then(r => r.json())
      .then(setPackageInfo)
      .catch(() => {});

    fetch(`${API}/api/deploy/config`)
      .then(r => r.json())
      .then(cfg => {
        if (!cfg) return;
        setEnvConfigured(!!cfg.configured);
        setEnvPasswordSet(!!cfg.password_set);
        if (cfg.protocol) setProtocol(cfg.protocol);
        if (cfg.host) setHost(cfg.host);
        if (cfg.port) setPort(String(cfg.port));
        if (cfg.username) setUsername(cfg.username);
        if (cfg.remote_directory) setRemoteDir(cfg.remote_directory);
      })
      .catch(() => {});
  }, [API]);

  const handleDeploy = async (e) => {
    e.preventDefault();
    setDeploying(true);
    setResult(null);
    try {
      const body = {
        protocol,
        host,
        port: parseInt(port),
        username,
        remote_directory: remoteDir,
      };
      // Only include password if user typed one; otherwise server uses env.
      if (password) body.password = password;
      const res = await fetch(`${API}/api/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ success: false, message: `Request failed: ${err.message}`, files_transferred: 0, errors: [] });
    }
    setDeploying(false);
  };

  const inputClass = 'w-full px-4 py-3 bg-white border border-gray-200 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]/20 transition-colors';
  const labelClass = 'flex items-center gap-2 text-xs font-medium text-gray-600 uppercase tracking-wider mb-2';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="deploy-page">
      {/* Header */}
      <header className="bg-black py-4 px-4 md:px-8">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="deploy-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="tracking-wide">Back to Website</span>
          </a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-7 w-auto" />
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12 md:py-16">
        {/* Deploy Section */}
        <div className="bg-white border border-gray-200 p-8 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-[#C9A227]/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-[#C9A227]" />
            </div>
            <div>
              <h1 className="text-xl font-medium text-black">Deploy Website</h1>
              <p className="text-gray-500 text-sm">Transfer files to your hosting account</p>
            </div>
          </div>

          {/* Security Note */}
          <div className="mt-6 mb-8 p-4 bg-[#C9A227]/5 border border-[#C9A227]/20 flex items-start gap-3">
            <Shield className="w-4 h-4 text-[#C9A227] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">
              <span className="font-medium">Security Note:</span> Your credentials are used only for this transfer and are not stored unless saved server-side in environment variables.
            </p>
          </div>

          {envConfigured && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 flex items-start gap-3" data-testid="deploy-env-banner">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">FTP credentials are saved on the server.</span> The form below is pre-filled. Click <span className="font-medium">Deploy</span> to push the latest build — no need to re-type the password.
              </p>
            </div>
          )}

          <form onSubmit={handleDeploy} className="space-y-6" data-testid="deploy-form">
            {/* Protocol */}
            <div>
              <label className={labelClass}>Transfer Protocol</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="protocol"
                    value="ftp"
                    checked={protocol === 'ftp'}
                    onChange={() => { setProtocol('ftp'); setPort('21'); }}
                    className="accent-[#C9A227]"
                  />
                  <span className="text-sm text-gray-700">FTP</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="protocol"
                    value="sftp"
                    checked={protocol === 'sftp'}
                    onChange={() => { setProtocol('sftp'); setPort('22'); }}
                    className="accent-[#C9A227]"
                  />
                  <span className="text-sm text-gray-700">SFTP (SSH)</span>
                </label>
              </div>
            </div>

            {/* Host + Port */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className={labelClass}>
                  <Globe className="w-3.5 h-3.5" /> Host / Server
                </label>
                <input
                  type="text"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="ftp.yourdomain.com"
                  required
                  className={inputClass}
                  data-testid="deploy-host"
                />
              </div>
              <div>
                <label className={labelClass}>Port</label>
                <input
                  type="number"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className={inputClass}
                  data-testid="deploy-port"
                />
              </div>
            </div>

            {/* Username */}
            <div>
              <label className={labelClass}>
                <User className="w-3.5 h-3.5" /> Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your_ftp_username"
                required
                className={inputClass}
                data-testid="deploy-username"
              />
            </div>

            {/* Password */}
            <div>
              <label className={labelClass}>
                <Lock className="w-3.5 h-3.5" /> Password
                {envPasswordSet && (
                  <span className="ml-2 text-[10px] text-[#C9A227] normal-case tracking-normal" data-testid="deploy-password-env">
                    (saved in server env — leave blank to use it)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={envPasswordSet ? 'Using saved password' : '••••••••'}
                required={!envPasswordSet}
                className={inputClass}
                data-testid="deploy-password"
              />
            </div>

            {/* Remote Directory */}
            <div>
              <label className={labelClass}>
                <FolderOpen className="w-3.5 h-3.5" /> Remote Directory
              </label>
              <input
                type="text"
                value={remoteDir}
                onChange={(e) => setRemoteDir(e.target.value)}
                placeholder="/public_html"
                className={inputClass}
                data-testid="deploy-remote-dir"
              />
              <p className="text-xs text-gray-400 mt-1">Usually /public_html for cPanel or /var/www/html for servers</p>
            </div>

            {/* Package Info */}
            {packageInfo?.ready && (
              <div className="p-4 bg-gray-50 border border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="w-4 h-4 text-[#1e3a5f]" />
                  <div>
                    <p className="text-sm text-gray-700 font-medium">{packageInfo.file_count} files ready</p>
                    <p className="text-xs text-gray-400">{packageInfo.total_size_mb} MB total</p>
                  </div>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            )}

            {/* Deploy Button */}
            <button
              type="submit"
              disabled={deploying}
              className="w-full py-4 bg-[#1e3a5f] text-white text-sm tracking-[0.15em] uppercase font-medium hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              data-testid="deploy-submit"
            >
              {deploying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Transferring Files...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Deploy to Server
                </>
              )}
            </button>
          </form>

          {/* Result */}
          {result && (
            <div className={`mt-6 p-4 border ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="deploy-result">
              <div className="flex items-center gap-2 mb-2">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`text-sm font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                  {result.message}
                </span>
              </div>
              {result.files_transferred > 0 && (
                <p className="text-xs text-gray-500">{result.files_transferred} files transferred</p>
              )}
              {result.errors?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {result.errors.slice(0, 5).map((err, i) => (
                    <li key={i} className="text-xs text-red-600">{err}</li>
                  ))}
                  {result.errors.length > 5 && (
                    <li className="text-xs text-red-400">...and {result.errors.length - 5} more errors</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Developer Install Guide */}
        <div className="bg-white border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#1e3a5f]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#1e3a5f]" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-black">Developer Installation Guide</h2>
              <p className="text-gray-500 text-sm">Step-by-step cPanel deployment instructions, SSL setup, troubleshooting, and access codes</p>
            </div>
          </div>

          <a
            href="/INSTALL_GUIDE.txt"
            download
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#1e3a5f] text-[#1e3a5f] text-xs tracking-wider uppercase font-medium hover:bg-[#1e3a5f] hover:text-white transition-colors"
            data-testid="download-guide"
          >
            <Download className="w-3.5 h-3.5" />
            Download Install Guide (.txt)
          </a>
        </div>
      </div>

      <footer className="bg-black py-6 px-4 text-center">
        <p className="text-white/40 text-xs tracking-wider">Sebastian Alliance Group &bull; Website Deployment</p>
      </footer>
    </div>
  );
};

export default DeployPage;
